import Vue from "vue"
import { MutationTree, ActionTree, GetterTree } from "vuex"

import buildInfluencer from "~/helpers/influencer/build"
import resetMixin from "~/mixins/store/resetToInitialState"

import { RootState } from "~/store"
import Influencer from "~/types/influencer"
export interface FetchConfig {
  offset: number
  limit: number
  noFollow?: boolean
  noFetchRecommendation?: boolean
}
interface ApiResponse {
  count: number
  next: null | string
  results: Influencer[]
}

const initialState = () => {
  return {
    list: [] as Influencer[],
    default_config: {
      offset: 0,
      limit: 128,
    } as FetchConfig,
  }
}
export const state = initialState
export type InfluencersState = ReturnType<typeof state>

export const mutations: MutationTree<InfluencersState> = {
  SET_INFLUENCER_ARRAY(state, influencers: Influencer[]) {
    Vue.set(state, "list", influencers)
  },
  ADD_INFLUENCER_ARRAY(state, influencers: Influencer[]) {
    Vue.set(state, "list", state.list.concat(influencers))
  },
  REMOVE_INFLUENCER_AT_INDEX(state, index: number) {
    state.list.splice(index, 1)
  },
  ...resetMixin.mutations,
}

export const actions: ActionTree<InfluencersState, RootState> = {
  RESET({ commit }) {
    commit("RESET", initialState())
  },
  FETCH_FROM_RECO({ dispatch, state }): Promise<void> {
    return new Promise((resolve, reject) => {
      dispatch("recommendation/FETCH_RECOMMENDATION")
        .then(() => {
          //@ts-ignore
          const influencerIds: number[] = state.recommendation.ranks

          dispatch("RESET")
          dispatch("FETCH_SET_AXIOS", influencerIds)
            .then(async (response: ApiResponse) => {
              await dispatch("HANDLE_INFLUENCER_BATCH", {
                response,
                config: {
                  noFollow: true,
                  noFetchRecommendation: true,
                } as FetchConfig,
              })
              resolve()
            })
            .catch((err) => {
              console.log(err)
              reject(err)
            })
        })
        .catch((err) => {
          console.log(err)
          reject(err)
        })
    })
  },
  FETCH_FROM_BACKEND(
    { dispatch, state },
    config: FetchConfig = { ...state.default_config }
  ) {
    return new Promise((resolve, reject) =>
      this.$axios
        .$get(
          `/influencer/statsv3/?limit=${config.limit}&offset=${config.offset}`
        )
        .then(async (response: ApiResponse) => {
          config.offset += config.limit
          resolve(
            await dispatch("HANDLE_INFLUENCER_BATCH", { config, response })
          )
        })
        .catch((err) => {
          reject(err)
        })
    )
  },
  FETCH_SET_AXIOS({ state }, influencerIdArray: (string | number)[]) {
    const maxIdCount = 128
    const fetching = influencerIdArray
      .map((influencerId) => Number(influencerId))
      .filter((influencerId) => {
        return !state.list?.some(
          (influencer: Influencer) => influencer.id === influencerId
        )
      })

    return new Promise((resolve, reject) => {
      if (fetching.length) {
        Promise.all(
          fetching
            .reduce(
              (accumulator, influencerId) => {
                const lastMember: number[] = accumulator[accumulator.length - 1]

                if (lastMember.length >= maxIdCount)
                  accumulator.push([] as number[])
                accumulator[accumulator.length - 1].push(influencerId)
                return accumulator
              },
              [[]] as number[][]
            )
            .map((idArray) => {
              return this.$axios.$get(
                `/influencer/statsv3/?bulk=${idArray.toString()}`
              )
            })
        )
          .then((responses: ApiResponse[]) => {
            const response = responses.reduce(
              (accumulator, response) => {
                accumulator.results.push(...response.results)
                accumulator.count += response.count
                return accumulator
              },
              { next: null, results: [], count: 0 } as ApiResponse
            )

            response.results.sort((a, b) => {
              return fetching.indexOf(b.id) - fetching.indexOf(a.id)
            })
            resolve(response)
          })
          .catch((err) => {
            console.log(err)
            reject(err)
          })
      } else resolve({ next: null, results: [], count: 0 } as ApiResponse)
    })
  },
  FETCH_SET({ dispatch }, influencerIdArray: (string | number)[]) {
    return new Promise((resolve) => {
      dispatch("FETCH_SET_AXIOS", influencerIdArray)
        .then(async (response: ApiResponse) => {
          await dispatch("HANDLE_INFLUENCER_BATCH", {
            response,
            config: { noFollow: true },
          })
          resolve(response.results.map((e) => e.id))
        })
        .catch(() => {
          resolve({ next: null, results: [], count: 0 } as ApiResponse)
        })
    })
  },
  HANDLE_INFLUENCER_BATCH(
    { rootState, getters, commit, dispatch },
    { response, config }: { response: ApiResponse; config: FetchConfig }
  ) {
    return new Promise((resolve) => {
      const { next, results } = response
      const ids = [...new Set(results.map((e) => e.id))]
      const batch = ids
        .filter((influencerId) => {
          const raw_influencer = results.find((e) => e.id === influencerId)

          if (raw_influencer)
            return (
              !getters.IDS.includes(raw_influencer.id) &&
              (!raw_influencer.is_premium ||
                //@ts-ignore
                rootState.user.agency.is_premium) &&
              (!raw_influencer.is_scout ||
                //@ts-ignore
                rootState.user.email === "agency@groover.co")
            )
          else return false
        })
        .reduce((accumulator, influencerId: number) => {
          accumulator.push(
            buildInfluencer(
              results.find(function (influencer: Influencer) {
                return influencer.id === influencerId
              })
            ) as Influencer
          )
          return accumulator
        }, [] as Influencer[])
      dispatch(
        "recommendation/FETCH_FROM_INFLUENCER_IDS",
        config.noFetchRecommendation !== true ? ids : []
      ).finally(() => {
        if (batch.length) commit("ADD_INFLUENCER_ARRAY", batch)
        if (process.client && config.noFollow !== true && next) {
          resolve(dispatch(`FETCH_FROM_BACKEND`, config))
        } else resolve(true)
      })
    })
  },
  FETCH_SLUG(_, slug): Promise<Influencer> {
    return new Promise((resolve, reject) => {
      this.$axios
        .$get(`/influencer/profile/${slug}/`)
        .then(async (response: Influencer) => {
          resolve(buildInfluencer(response) as Influencer)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
  REMOVE_ONE({ state, commit }, { slug, id }: { slug?: string; id?: number }) {
    const fns = {
      slug(e: Influencer) {
        return e.slug === slug
      },
      id(e: Influencer) {
        return e.id === id
      },
    }
    const index = state.list.findIndex(fns[slug ? "slug" : "id"])

    if (index >= 0) commit("REMOVE_INFLUENCER_AT_INDEX", index)
  },
}

export const getters: GetterTree<InfluencersState, RootState> = {
  IDS(state) {
    return state.list.map((e) => e.id)
  },
  GET_BY_ID(state) {
    return function (id: number) {
      return state.list.find((e) => e.id === id)
    }
  },
  GET_BY_IDS: (state) => (ids: number[]) =>
    state.list
      .filter((e) => ids.includes(e.id))
      .sort((a, b) => {
        return ids.indexOf(a.id) - ids.indexOf(b.id)
      }),
  GET_BY_SLUG(state) {
    return function (slug: string) {
      return state.list.find((e) => e.slug === slug)
    }
  },
  ID_EXISTS(state) {
    return function (influencerId: number) {
      return state.list.some((influencer) => influencer.id === influencerId)
    }
  },
  SLUG_EXISTS(state) {
    return function (influencerSlug: string) {
      return state.list.some((e) => {
        return e.slug === influencerSlug
      })
    }
  },
}
